import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { FilterCategory, IFilterCategoryConfig } from '@df/catalog-ui/listing/filter/filter-category';
import { IFilterDefinition, IListingConfig, IListingSetup, Listing, TAttrOptionSelection } from '@df/catalog-ui/listing/listing';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import difference from 'lodash-es/difference';

export interface IProductListingConfig extends IListingConfig {
    /**
     * If in stock only are allowed (undefined means follow global settings)
     */
    stockCheck?: boolean;
    /**
     * If routable only models are allowed
     */
    routableCheck?: boolean;
    /**
     * If OOS products hsould be pushed to bottom
     */
    pushOOStoBottom?: boolean;
}

export class ProductListing<T extends Product = Product<IProductDataV2>> extends Listing<T> {
    initModels!: T[];

    /**
     * Extend config
     */
    override config!: IProductListingConfig;

    constructor(config: Partial<IProductListingConfig>, models: T[] | number[], setup: IListingSetup<T> = {}) {
        super(config, models, setup);
    }

    /**
     * Checks if stock filter should be applied
     */
    shouldCheckStock(): boolean {
        return this.config.stockCheck === undefined ? !this.app.data.showOutOfStock : this.config.stockCheck;
    }

    /**
     * Models setter
     */
    override setSourceModels(models: T[]): this {
        super.setSourceModels(models);

        this.initModels = this.modelsBare;

        // apply stock check if requested
        if (this.shouldCheckStock()) {
            this.modelsBare = this.modelsBare.filter(i => this.checkStock(i));
        }

        if (this.config.routableCheck) {
            this.modelsBare = this.modelsBare.filter(i => !!(i.data && (<IProductDataV2>i.data).url));
        }

        return this;
    }

    private checkStock(product: T): boolean {
        return !product.isOutOfStock;
    }

    /**
     * Called when sorting models is ready
     */
    protected override sortModelsCb(_models: T[]) {
        // push OOS items to the bottom
        if (this.config.pushOOStoBottom) {
            const oosModels = _models.filter(model => model.isOutOfStock || model.stock?.data?.isOutTemp);
            _models = [...difference(_models, oosModels), ...oosModels];
        }
        return super.sortModelsCb(_models);
    }

    /**
     * Additional filtration to apply post regular filters
     * we use mainly on product-listing to apply deep stock checking
     */
    protected override postFilterCheck(models: T[], selection: TAttrOptionSelection, overrides?: { filter: Filter<T>; selection: any }[]) {
        if (this.shouldCheckStock()) {
            return models.filter(
                model => model.deepStockCheck && typeof model.deepStockCheck === 'function' && model.deepStockCheck(selection, true)
            );
        }
        return models;
    }

    protected override createFilter(filterDef: IFilterDefinition): void {
        if (filterDef.type === 'category') {
            const sourceCategory = this.config.source;

            if (sourceCategory && sourceCategory.id) {
                const filterConfig: IFilterCategoryConfig = {
                    id: sourceCategory.id,
                    type: filterDef.type,
                    source: filterDef.source,
                    position: <number>filterDef.position,
                    isOpen: !!filterDef.is_open
                };

                this.addFilter(new FilterCategory<T>(filterConfig, this));
            } else {
                this.logger.warn('Category filter requires source category');
            }
        } else {
            super.createFilter(filterDef);
        }
    }

    hasNoResults() {
        return;
    }
}
